import { Component, computed, effect, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges, input } from '@angular/core';
import { LabelViewerComponent } from '../../label-viewer/label-viewer.component';
import { MinimapIconComponent } from '../../icons/minimap-icon/minimap-icon.component';
import { HighlightBeamIconComponent } from '../../icons/highlight-beam-icon/highlight-beam-icon.component';
import { TooltipWrapperComponent } from '../../tooltip-wrapper/tooltip-wrapper.component';
import { CosmeticOptions, CustomRuleStyle, Displayable, DisplayOptions, Filter } from '../../../models/filter-models';
import { DisplayType } from '../../../models/enums';
import { Rarity } from '../../../models/enums';
import { RuleId } from '../../../models/rule-id';
import { StylePickerDialogComponent } from '../style-picker-dialog/style-picker-dialog.component';
import { CosmeticsGeneratorService } from '../../../services/cosmetics-generator.service';
import { FilterStoreService } from '../../../services/filter-store.service';
import { SettingsService } from '../../../services/settings.service';
import { labelPreviewBgImageStyle } from '../../label-preview-backgrounds';

@Component({
  selector: 'app-custom-rule-style-picker',
  imports: [MinimapIconComponent, HighlightBeamIconComponent, TooltipWrapperComponent, LabelViewerComponent, StylePickerDialogComponent],
  templateUrl: './custom-rule-style-picker.component.html',
  styleUrls: ['../../rule-preview/label-preview-shared.scss', '../style-picker.component.scss', './custom-rule-style-picker.component.scss']
})
export class CustomRuleStylePickerComponent implements OnInit, OnChanges {
  private readonly settings = inject(SettingsService);
  private readonly filterStoreService = inject(FilterStoreService);

  labelBackgroundImage = computed(() => labelPreviewBgImageStyle(this.settings.labelPreviewBg()));

  filter = input.required<Filter>();
  ruleId = input.required<RuleId>();

  private readonly ruleStyleRevision = signal(0);

  hasRuleStyle = computed(() => {
    this.ruleStyleRevision();
    return !!this.findRuleStyle();
  });

  @Input({ required: true }) active!: boolean;

  @Input() disabled: boolean = false;
  @Input() exampleName?: string;
  @Input() defaultVisible: boolean = false;
  @Input() defaultHidden: boolean = false;

  @Input() rarity?: Rarity;
  @Input() isCurrency?: boolean;
  @Input() isWaystone?: boolean;
  @Input() isPoe1Gem?: boolean;
  @Input() isQuestItem?: boolean;
  @Input() title?: string;
  @Input() sockets?: string;
  @Input() influence?: string;
  @Input() fractured?: boolean;
  @Input() veiled?: boolean;
  @Input() synthesised?: boolean;

  @Input() right = true;

  @Output() styleUpdated = new EventEmitter<void>();

  DisplayType = DisplayType;

  dialogOpen = false;

  /** Dummy displayable for StylePickerDialog; cosmeticOptions mirror customRuleStyles entry when present. */
  dummyDisplayable: Displayable = {
    displayOptions: new DisplayOptions({
      displayType: DisplayType.Custom,
      active: true,
      hidden: false,
      cosmeticOptions: new CosmeticOptions(),
    }),
  };

  cosmeticOptions: CosmeticOptions = new CosmeticOptions();
  hidden = false;

  private initialFilterChangeCounter: number;

  constructor(private cosmeticsGeneratorService: CosmeticsGeneratorService) {
    this.initialFilterChangeCounter = this.filterStoreService.dirtyFilterChanged();

    effect(() => {
      const currentCounter = this.filterStoreService.dirtyFilterChanged();
      if (currentCounter !== this.initialFilterChangeCounter) {
        this.syncDummyFromFilter();
        this.setCosmeticOptionsFromDisplayOptions();
      }
    });
  }

  ngOnInit(): void {
    this.syncDummyFromFilter();
    this.setCosmeticOptionsFromDisplayOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.syncDummyFromFilter();
    this.setCosmeticOptionsFromDisplayOptions();
  }

  findRuleStyle(): CustomRuleStyle | undefined {
    return this.filterStoreService.activeFilter().customRuleStyles.find(s => s.ruleId === this.ruleId());
  }

  syncDummyFromFilter(): void {
    this.ruleStyleRevision.update(n => n + 1);
    const entry = this.findRuleStyle();
    const opts = entry ? new CosmeticOptions({ ...entry.options }) : new CosmeticOptions();
    this.dummyDisplayable.displayOptions.displayType = DisplayType.Custom;
    this.dummyDisplayable.displayOptions.active = this.active;
    this.dummyDisplayable.displayOptions.hidden = !this.active && this.defaultHidden;
    this.dummyDisplayable.displayOptions.cosmeticOptions = opts;
  }

  setCosmeticOptionsFromDisplayOptions(): void {
    const result = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(
      this.dummyDisplayable.displayOptions,
      this.exampleName,
      this.title
    );
    Object.assign(this.cosmeticOptions, result.cosmeticOptions);
    this.hidden = result.hidden;
  }

  upsertCustomRuleStyleFromDummy(): void {
    const opts = new CosmeticOptions({ ...this.dummyDisplayable.displayOptions.cosmeticOptions });
    const filter = this.filter();
    const ruleId = this.ruleId();
    const idx = filter.customRuleStyles.findIndex(s => s.ruleId === ruleId);
    const row: CustomRuleStyle = {
      ruleId,
      custom: true,
      partial: false,
      options: opts,
    };
    if (idx >= 0) {
      filter.customRuleStyles[idx] = row;
    } else {
      filter.customRuleStyles.push(row);
    }
  }

  onDialogStyleUpdated(): void {
    this.upsertCustomRuleStyleFromDummy();
    this.syncDummyFromFilter();
    this.setCosmeticOptionsFromDisplayOptions();
    this.styleUpdated.emit();
  }

  onDialogCustomRuleStyleCleared(): void {
    this.syncDummyFromFilter();
    this.setCosmeticOptionsFromDisplayOptions();
    this.styleUpdated.emit();
  }

  openStylePickerDialog(): void {
    if (!this.disabled) {
      this.dialogOpen = true;
    }
  }

  closeStylePickerDialog(): void {
    this.dialogOpen = false;
  }
}
